import { getLocalToday } from './dateUtils';

/**
 * Formats a date string to MM/DD/YYYY format
 * @param {string} dateString - ISO date string or date string
 * @returns {string|null} Formatted date string or null if invalid
 */
export function formatDate(dateString) {
  if (!dateString) return null;

  // If the date string is in YYYY-MM-DD format, parse it directly to avoid timezone issues
  const dateOnlyMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return `${month}/${day}/${year}`;
  }

  // For other formats, use Date parsing
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
}

/**
 * Formats a date string to short format (e.g., "Dec 22, 2025")
 * @param {string} dateString - ISO date string or YYYY-MM-DD string
 * @returns {string} Formatted date string or empty string if invalid
 */
export function formatDateShort(dateString) {
  if (!dateString) return '';

  // Handle YYYY-MM-DD format to avoid timezone issues
  const dateOnlyMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Formats a date string to long format (e.g., "Sunday, December 22, 2025")
 * @param {string} dateString - ISO date string or YYYY-MM-DD string
 * @returns {string} Formatted date string or empty string if invalid
 */
export function formatDateLong(dateString) {
  if (!dateString) return '';

  // Handle YYYY-MM-DD format to avoid timezone issues
  const dateOnlyMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Formats a phone number to (XXX) XXX-XXXX format
 * @param {string} phoneNumber - Raw phone number string
 * @returns {string|null} Formatted phone number or null if invalid
 */
export function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber) return null;

  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  return phoneNumber;
}

/**
 * Formats a number as USD currency
 * @param {number} cents - Amount to format
 * @returns {string|null} Formatted currency string or null if invalid
 */
export function formatCurrency(cents) {
  if (cents === null || cents === undefined) return null;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(cents / 100);
}

/**
 * Calculates ticket status based on payment amounts and dates
 * @param {object} ticket - Ticket object with amountCentsBalance, dateNextReceivable, isCancelled, isDeleted
 * @returns {string} Status: 'Deleted', 'Cancelled', 'Fully Paid', 'Late Payment', or 'In Progress'
 */
export function calculateTicketStatus(ticket) {
  const { amountCentsBalance, dateNextReceivable, isCancelled, isDeleted } = ticket;
  // Only the root admin can load a deleted ticket at all, so this is what they see there
  if (isDeleted) return 'Deleted';
  if (isCancelled) return 'Cancelled';
  if (amountCentsBalance <= 0) return 'Fully Paid';
  if (dateNextReceivable) {
    const today = getLocalToday();
    if (dateNextReceivable < today) return 'Late Payment';
  }
  return 'In Progress';
}

/**
 * Calculates receivable status based on due date, balance, and cancelled amount
 * @param {object} receivable - Receivable object with dateDue, amountCentsBalance, and amountCentsCancelled
 * @returns {string} Status: 'Cancelled', 'Paid', 'Overdue', 'Due Today', or 'Upcoming'
 */
export function calculateReceivableStatus(receivable) {
  const { dateDue, amountCentsBalance, amountCentsCancelled } = receivable;
  if (amountCentsCancelled > 0) return 'Cancelled';
  if (amountCentsBalance === 0) return 'Paid';
  const today = getLocalToday();
  if (dateDue < today) return 'Overdue';
  if (dateDue === today) return 'Due Today';
  return 'Upcoming';
}
